import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { StatusBadge, PriorityBadge, StageBadge } from '@/components/StatusBadge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { NEXT_STEP_LABELS, type DealStatus } from '@/types';
import { AlertTriangle, X } from 'lucide-react';
import { timeAgo } from '@/data/mockData';
import { useDealershipData } from '@/data/hooks/useDealershipData';

const STATUS_OPTIONS: { value: DealStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'slowing', label: 'Slowing' },
  { value: 'no_activity', label: 'No Activity' },
];

export default function DealsList() {
  const { deals, loading } = useDealershipData();

  const [statusFilter, setStatusFilter] = useState<DealStatus | 'all'>('all');
  const [repFilter, setRepFilter] = useState<string>('all');

  const repOptions = useMemo(() => {
    const unique = Array.from(new Map(deals.map(d => [d.rep_id, d.rep_name])).entries());
    return [{ value: 'all', label: 'All Reps' }, ...unique.map(([id, name]) => ({ value: id, label: name }))];
  }, [deals]);

  const filteredDeals = useMemo(() => {
    return deals.filter(d => {
      const statusMatch = statusFilter === 'all' || d.status === statusFilter;
      const repMatch = repFilter === 'all' || d.rep_id === repFilter;
      return statusMatch && repMatch;
    });
  }, [deals, statusFilter, repFilter]);

  const hasFilters = statusFilter !== 'all' || repFilter !== 'all';

  const clearFilters = () => {
    setStatusFilter('all');
    setRepFilter('all');
  };

  if (loading) {
    return (
      <div className="space-y-6 pb-20 lg:pb-0">
        <div>
          <h1 className="text-2xl font-bold">View Deals</h1>
          <p className="text-sm text-muted-foreground">Loading deals…</p>
        </div>
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4 h-20" />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20 lg:pb-0">
      <div>
        <h1 className="text-2xl font-bold">View Deals</h1>
        <p className="text-sm text-muted-foreground">{filteredDeals.length} deals in pipeline</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as DealStatus | 'all')}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={repFilter} onValueChange={setRepFilter}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Filter by rep" />
          </SelectTrigger>
          <SelectContent>
            {repOptions.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1 text-muted-foreground">
            <X className="h-4 w-4" /> Clear
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {filteredDeals.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-12">No deals match the selected filters.</p>
        ) : (
          filteredDeals.map(deal => (
            <Link key={deal.id} to={`/deals/${deal.id}`}>
              <Card className="hover:border-primary/30 transition-colors">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{deal.customer_name}</span>
                      <PriorityBadge priority={deal.priority} />
                      <StageBadge stage={deal.stage} />
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{deal.vehicle} · {deal.rep_name}</p>
                    {deal.next_step ? (
                      <span className="text-xs text-muted-foreground">Next: {NEXT_STEP_LABELS[deal.next_step]}</span>
                    ) : deal.stage !== 'sold' ? (
                      <span className="text-xs text-red-400 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> No next step</span>
                    ) : null}
                  </div>
                  <div className="text-right shrink-0 ml-4 space-y-1">
                    <p className="font-bold">${deal.estimated_value.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">{timeAgo(deal.last_activity_at)}</p>
                    <StatusBadge status={deal.status} />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
